import AppGlobalsContext from "app/AppGlobalsContext";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import IconDeparting from "app/pages/.shared/IconDeparting";
import IconLocation from "app/pages/.shared/IconLocation";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ClearIndicatorSdpSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/ClearIndicatorSdpSearchInput";
import DestinationInputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/DestinationInputValueFormat";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import {
	defaultValues,
	validateDepartureCity,
	validateDestination,
	validateSearch,
} from "app/pages/SmartDP/Search/SDPSearchForm/smartDPSearchFormSchema";
import SmartDPSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchInput";
import TravellersRoomButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomButton";
import { destinationResortFilterOptions, isServerSide, sortOptions } from "app/utils/utils";
import classNames from "classnames";
import { differenceInDays } from "date-fns";
import { Form, Formik } from "formik";
import get from "lodash/get";
import PropTypes from "prop-types";
import { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import { DESTINATION_USA_CODE, FS_QUOTE_EVENT_NAME, WEBSITE_TUNNEL_TYPES } from "app/constants";
import CalendarDisabledView from "app/pages/SmartDP/Search/SDPSearchForm/CalendarDisabledView";
import TravellersRoomDrawerButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomDrawerButton";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { brandPropTypes } from "app/utils/propTypes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import InputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/InputValueFormat";
import StopoverDateCalendarContainer from "app/pages/Stopover/StopoverSearchForm/StopoverDateCalendarContainer";
import IconSearchFormDropdown from "app/pages/.shared/static/icons/IconSearchFormDropdown";

import "app/pages/.shared/form/SearchForm.scss";
import "./StopoverSearchForm.scss";
import LastSearchContainer from "app/features/SearchEngine/LastSearch/LastSearchContainer";

const SmartDPSearchForm = ({
	onSubmit = () => {},
	onSuccess = () => {},
	departureCities = [],
	stopoverCities = [],
	stopoverDirections = [],
	destinations = [],
	cabins = [],
	initialValues = {},
	validateOnMount,
	displayLoader,
	errorScrollIntoView = true,
	onFieldFocus = () => {},
	isAutoScrollActive,
	resetAllSDPProductsFilter = () => {},
}) => {
	const { hit: fsHit } = useFlagship();

	const stopoverCitiesWithIatas = stopoverCities.map(city => ({
		...city,
		label: `${city.label} (${city.iatas})`,
	}));

	const showStopoverCityInput = stopoverCitiesWithIatas?.length > 1;

	const selectRefs = {
		departureCity: useRef(null),
		destinationResort: useRef(null),
		departureDate: useRef(null),
		cabin: useRef(null),
		stopoverCity: useRef(null),
		stopoverDirection: useRef(null),
	};

	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const handleSubmit = useCallback(
		(values, actions) => {
			resetAllSDPProductsFilter();
			actions.setSubmitting(false);
			onSuccess({ values, pathname });
		},
		[departureCities, destinations, cabins, pathname]
	);
	const [destinationInput, setDestinationInput] = useState();
	const [departureInput, setDepartureInput] = useState();

	// fix pour ne pas avoir les labels sur 2 lignes au premier rendu
	const mergeInitialValues = isServerSide
		? {
				occupancies: [{ adults: 2, children: 0, childrenBirthdates: [] }],
		  }
		: {
				...defaultValues,
				...initialValues,
		  };

	const isListingOrQuote =
		pathname.includes("/stopover/listing") || pathname === "/stopover/booking/quote";

	const hasCabins = isServerSide || cabins.length > 0;

	const onHandleClickSearchButton = useCallback(() => {
		onSubmit();
		fsHit.send({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_QUOTE_EVENT_NAME.CLICK_ON_SEARCH_CTA,
		});
	}, []);

	const [refs, setRefs] = useState(null);

	const updateFloatingPositionReference = useCallback(refs => {
		setRefs(refs);
	}, []);
	const customFieldIds = ["departureDate"];

	const fieldIds = useMemo(() => ["departureCity", "destinationResort", "departureDate"], []);

	const fieldOrder = useMemo(() => fieldIds.map(id => ({ id, ref: selectRefs[id] })), [
		fieldIds,
		selectRefs,
	]);
	const getFieldValue = (values, fieldId) => {
		const nestedFields = {
			departureDate: ["travelDates", "departureDate"],
		};

		if (nestedFields[fieldId]) {
			return nestedFields[fieldId].reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				values
			);
		}

		return values[fieldId];
	};

	const handleFieldChange = useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (value) {
				const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
				if (currentIndex === -1) {
					return;
				}

				const previousFields = fieldOrder.slice(0, currentIndex);
				const firstEmptyPrevField = previousFields.find(
					field => !getFieldValue(values, field.id)
				);

				if (firstEmptyPrevField && firstEmptyPrevField.ref.current) {
					if (customFieldIds.includes(firstEmptyPrevField.id)) {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.click();
						});
					} else {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.focus();
						});
					}
					return;
				}

				let nextIndex = currentIndex + 1;

				while (nextIndex < fieldOrder.length) {
					const nextField = fieldOrder[nextIndex];
					if (!getFieldValue(values, nextField.id) && nextField.ref.current) {
						requestAnimationFrame(() => {
							const event = new MouseEvent("click", {
								view: window,
								bubbles: true,
								cancelable: true,
							});
							nextField.ref.current.dispatchEvent(event);
							nextField.ref.current.click();
						});
						break;
					}
					nextIndex++;
				}
			}
		},
		[fieldOrder, customFieldIds]
	);

	return (
		<div
			className={classNames("search-form search-form--stopover", {
				"search-form--stopover-with-cabins": hasCabins,
			})}
			ref={refs?.setPositionReference}
		>
			<Formik
				enableReinitialize
				initialValues={mergeInitialValues}
				validate={validateSearch(false)}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={
					validateOnMount && {
						departureCity: true,
						destinationResort: true,
						travelDates: {
							departureDate: true,
							endDate: true,
						},
						duration: {
							code: true,
						},
						occupancies: true,
						cabin: true,
					}
				}
				validateOnMount={validateOnMount}
			>
				{({ values, errors, isValid, isSubmitting, setFieldValue, submitCount }) => {
					const handleTravellersConfirmation = values => {
						setFieldValue("occupancies", values);
					};

					const isUsaDestination = DESTINATION_USA_CODE.includes(
						values?.destinationResort?.code
					);

					return (
						<>
							<Form
								className="search-form__form search-form--stopover"
								data-testid="sdp-search-form"
							>
								<SmartDPSearchInput
									menuIsOpen={Boolean(departureInput)}
									id="departureCity"
									name="departureCity"
									selectRef={selectRefs.departureCity}
									openMenuOnFocus={!values.departureCity}
									openMenuOnClick={!values.departureCity}
									onChange={value =>
										handleFieldChange(
											"departureCity",
											setFieldValue,
											value,
											values
										)
									}
									className="search-form__field search-form__field-departure"
									data-testid="departure-city-input"
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									validate={validateDepartureCity}
									label={
										<FormattedMessage id="sdp.search.departure.city.input.label" />
									}
									drawerInputLabel={
										<FormattedMessage id="sdp.search.departure.city.input.label" />
									}
									drawerTitleLabel={
										<FormattedMessage id="drawer.departure.title" />
									}
									components={{
										SingleValue: InputValueFormat,
										MenuList: LocationMenuList,
										ClearIndicator: ClearIndicatorSdpSearchInput,
									}}
									formatOptionLabel={({ label = "" }) => {
										return (
											<div className="search-form__suggestion">
												{!isMobile && (
													<div className="search-form__suggestion-picto">
														<IconDeparting />
													</div>
												)}
												<span
													dangerouslySetInnerHTML={{
														__html: label,
													}}
												/>
											</div>
										);
									}}
									getOptionValue={({ code }) => code}
									getOptionLabel={({ label }) => label}
									options={departureCities}
									// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
									// valeur par defaut
									value={
										departureCities.find(({ code }) => {
											return code === get(values, "departureCity.code");
										}) || ""
									}
									noOptionsMessage={() => {
										return <FormattedMessage id="sdp.search.no.result.label" />;
									}}
									popperWidth={360}
									popperOffset={[-16, 2]}
									locationMenuLabel={
										<FormattedMessage id="sdp.search.top.departure.cities.label" />
									}
									isTopLocationMenuDisplayed={false}
									onInputChange={inputValue => {
										setDepartureInput(inputValue);
									}}
									loading={displayLoader}
									isFloating
									floatingConfig={{
										placement: "bottom-start",
										offset: ({ rects }) => ({
											mainAxis: -rects.reference.height - 16,
											crossAxis: -16,
										}),
									}}
									placeholder={
										<FormattedMessage id="sdp.search.departure.city.input.label.mobile" />
									}
									showPlaceholderOnlyOnFocus={true}
								/>
								<SmartDPSearchInput
									menuIsOpen={Boolean(destinationInput)}
									id="destinationResort"
									name="destinationResort"
									selectRef={selectRefs.destinationResort}
									openMenuOnFocus={!values.destinationResort}
									openMenuOnClick={!values.destinationResort}
									validate={validateDestination}
									onChange={value =>
										handleFieldChange(
											"destinationResort",
											setFieldValue,
											value,
											values
										)
									}
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field search-form__field-destination"
									data-testid="destination-resort-input"
									filterOption={destinationResortFilterOptions}
									label={
										<FormattedMessage id="sdp.search.destination.input.label" />
									}
									drawerInputLabel={
										<FormattedMessage id="sdp.search.destination.input.label" />
									}
									formatOptionLabel={({ labels = [] }) => {
										return (
											<div className="search-form__suggestion">
												<div className="search-form__suggestion-picto">
													<IconLocation />
												</div>
												<div className="search-form__suggestion-text">
													<span
														className="search-form__suggestion-first-label"
														dangerouslySetInnerHTML={{
															__html: labels?.slice(-1).join(", "),
														}}
													/>
													<span
														className="search-form__suggestion-second-label"
														dangerouslySetInnerHTML={{
															__html: labels?.slice(0, -1).join(", "),
														}}
													/>
												</div>
											</div>
										);
									}}
									components={{
										SingleValue: DestinationInputValueFormat,
										ClearIndicator: ClearIndicatorSdpSearchInput,
										MenuList: LocationMenuList,
									}}
									getOptionValue={({ code }) => code}
									getOptionLabel={({ label }) => label}
									options={sortOptions(destinations, destinationInput)}
									// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
									// valeur par defaut
									value={
										destinations.find(({ code }) => {
											return (
												code ===
												Number(get(values, "destinationResort.code"))
											);
										}) || ""
									}
									noOptionsMessage={() => {
										return <FormattedMessage id="sdp.search.no.result.label" />;
									}}
									popperWidth={360}
									popperOffset={[-16, 2]}
									locationMenuLabel={
										<FormattedMessage id="sdp.search.top.destinations.cities.label" />
									}
									isTopLocationMenuDisplayed={false}
									onInputChange={inputValue => {
										setDestinationInput(inputValue);
									}}
									loading={displayLoader}
									isFloating
									floatingConfig={{
										placement: "bottom-start",
										offset: ({ rects }) => ({
											mainAxis: -rects.reference.height - 16,
											crossAxis: -16,
										}),
									}}
									placeholder={
										<FormattedMessage id="sdp.search.destination.input.label.mobile" />
									}
									showPlaceholderOnlyOnFocus={true}
								/>
								<StopoverDateCalendarContainer
									id="travelDates"
									name="travelDates"
									data-testid="travel-date-input"
									selectRef={selectRefs.departureDate}
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field"
									popperOffset={[0, -58]}
									updateFloatingPositionReference={
										updateFloatingPositionReference
									}
									calendarPopoverWidth={1024}
									calendarDisabledView={
										<CalendarDisabledView
											mainMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="sdp.calendar.unavailable.date.label" />
												) : (
													<FormattedMessage id="sdp.calendar.no.destination.no.departure.city.label" />
												)
											}
											secondaryMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="sdp.calendar.unavailable.date.sublabel" />
												) : null
											}
										/>
									}
									handleDayChange={({
										selectedEndDate,
										selectedDepartureDate,
									}) => {
										if (selectedEndDate && selectedDepartureDate) {
											const daysDifference = differenceInDays(
												new Date(selectedEndDate),
												new Date(selectedDepartureDate)
											);
											setFieldValue("duration", {
												code: daysDifference,
											});
										}
									}}
									loading={displayLoader}
								/>
								{hasCabins && !isMobile && (
									<SmartDPSearchInput
										id="cabin"
										name="cabin"
										selectRef={selectRefs.cabin}
										openMenuOnFocus
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										className="search-form__field search-form__field-cabin"
										data-testid="cabin-input"
										isSearchable={false}
										getOptionLabel={({ label = "", suffix = "" }) =>
											`${label}${suffix ? `, ${suffix}` : ""}`
										}
										components={{
											SingleValue: InputValueFormat,
											ClearIndicator: ClearIndicatorSdpSearchInput,
										}}
										getOptionValue={({ code }) => code}
										options={cabins}
										value={cabins.find(({ code }) => {
											return code === get(values, "cabin.code");
										})}
										popperWidth={240}
										popperOffset={[-10, -40]}
										loading={displayLoader}
										{...!isMobile && {
											inputValueIcon: (
												<IconSearchFormDropdown width={16} height={16} />
											),
										}}
									/>
								)}
								{!isListingOrQuote && !isMobile && (
									<div style={{ width: "100%", height: 0 }} />
								)}

								<div className="search-form__stopover-fields">
									{showStopoverCityInput && (
										<SmartDPSearchInput
											id="stopoverCity"
											name="stopoverCity"
											openMenuOnFocus
											className="search-form__field search-form__field-stopovercity"
											data-testid="stopover-city-input"
											selectRef={selectRefs.stopoverCity}
											isSearchable={false}
											elementToScroll="smartdp-marketing-headline"
											{...isMobile && {
												label: (
													<FormattedMessage id="sdp.search.stopover.city.input.label" />
												),
											}}
											drawerTitleLabel={
												<FormattedMessage id="sdp.search.stopover.city.input.label.mobile" />
											}
											getOptionLabel={({ label = "" }) => `${label}`}
											components={{
												SingleValue: InputValueFormat,
												ClearIndicator: ClearIndicatorSdpSearchInput,
											}}
											getOptionValue={({ code }) => code}
											options={stopoverCitiesWithIatas}
											value={stopoverCitiesWithIatas.find(({ code }) => {
												return code === get(values, "stopoverCity.code");
											})}
											popperWidth={240}
											popperOffset={[-10, -40]}
											loading={displayLoader}
											{...!isMobile && {
												inputValueIcon: (
													<IconSearchFormDropdown
														width={16}
														height={16}
													/>
												),
											}}
											{...!isMobile && {
												prefixLabel: (
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														isBold
														component="p"
														className="prefix-label"
													>
														<FormattedMessage id="sdp.search.stopover.city.input.prefix.label" />
													</Typography>
												),
											}}
										/>
									)}
									<SmartDPSearchInput
										id="stopoverDirection"
										name="stopoverDirection"
										openMenuOnFocus
										className={classNames(
											"search-form__field search-form__field-stopoverdirection",
											{
												"search-form__field-stopoverdirection--has-stopoverCity-input": showStopoverCityInput,
											}
										)}
										data-testid="stopover-direction-input"
										selectRef={selectRefs.stopoverDirection}
										isSearchable={false}
										elementToScroll="smartdp-marketing-headline"
										{...isMobile && {
											label: (
												<FormattedMessage id="sdp.search.stopover.direction.input.label" />
											),
										}}
										drawerTitleLabel={
											<FormattedMessage id="sdp.search.stopover.direction.input.label.mobile" />
										}
										getOptionLabel={({ label = "" }) => `${label}`}
										components={{
											SingleValue: InputValueFormat,
											ClearIndicator: ClearIndicatorSdpSearchInput,
										}}
										getOptionValue={({ code }) => code}
										options={stopoverDirections}
										value={stopoverDirections.find(({ code }) => {
											return code === get(values, "stopoverDirection.code");
										})}
										popperWidth={240}
										popperOffset={[-10, -40]}
										loading={displayLoader}
										{...!isMobile && {
											inputValueIcon: (
												<IconSearchFormDropdown width={16} height={16} />
											),
										}}
										{...!isMobile && {
											prefixLabel: (
												<Typography
													variant={TYPOGRAPHY_VARIANTS.REGULAR}
													isBold
													component="p"
													className="prefix-label"
												>
													<FormattedMessage id="sdp.search.stopover.direction.input.prefix.label" />
												</Typography>
											),
										}}
									/>
								</div>

								{isMobile && (
									<>
										<TravellersRoomDrawerButton
											className={classNames(
												"search-form__field search-form__field-rooms",
												{
													"search-form__field--fullsize": !hasCabins,
												}
											)}
											id="travellersRooms"
											style={{ flex: 1 }}
											onConfirmation={handleTravellersConfirmation}
											initialValues={values.occupancies}
											label={
												<FormattedMessage id="sdp.search.travellers.input.label" />
											}
											validateOnMount={validateOnMount}
											warningMessage={isUsaDestination}
										/>

										{hasCabins && (
											<SmartDPSearchInput
												id="cabin"
												name="cabin"
												selectRef={selectRefs.cabin}
												openMenuOnFocus
												onFocus={onFieldFocus}
												isAutoScrollActive={isAutoScrollActive}
												className="search-form__field search-form__field-cabin"
												data-testid="cabin-input"
												isSearchable={false}
												label={
													<FormattedMessage id="sdp.search.cabin.input.label" />
												}
												getOptionLabel={({ label = "", suffix = "" }) =>
													`${label}${suffix ? `, ${suffix}` : ""}`
												}
												components={{
													SingleValue: InputValueFormat,
													ClearIndicator: ClearIndicatorSdpSearchInput,
												}}
												getOptionValue={({ code }) => code}
												options={cabins}
												value={cabins.find(({ code }) => {
													return code === get(values, "cabin.code");
												})}
												popperWidth={240}
												popperOffset={[0, 0]}
												loading={displayLoader}
											/>
										)}
									</>
								)}

								{!isMobile && (
									<TravellersRoomButton
										className={classNames(
											"search-form__field search-form__field-rooms",
											{
												"search-form__field--fullsize": !hasCabins,
											}
										)}
										id="travellersRooms"
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										onConfirmation={handleTravellersConfirmation}
										initialValues={values.occupancies}
										label={
											<FormattedMessage id="sdp.search.travellers.input.label" />
										}
										stickyMode={false}
										elementToScroll="smartdp-marketing-headline"
										validateOnMount={validateOnMount}
										loading={displayLoader}
										popperWidth={360}
										popperOffset={[80, 4]}
										warningMessage={isUsaDestination}
									/>
								)}

								{/* Do not display error message on validationOnMount (redirection from emirates) */}
								{!isValid && submitCount >= 1 && (
									<div className="search-form__error">
										<FormErrorMessages
											errors={errors}
											isSubmitting={isSubmitting}
											submitCount={submitCount}
											disableErrorsDetails={true}
											message={
												<FormattedMessage id="sdp.search.form.error.message" />
											}
											scrollInToView={errorScrollIntoView}
										/>
									</div>
								)}
								<div className="search-form__footer">
									<Button
										variant={BUTTON_VARIANTS.FILLED}
										size={BUTTON_SIZES.LARGE}
										label={
											<FormattedMessage
												id={
													isListingOrQuote
														? "sdp.search.refresh.label"
														: "sdp.search.cta.label"
												}
											/>
										}
										submit
										className="search-form__button"
										loading={isSubmitting}
										data-testid="search-form-button"
										onClick={onHandleClickSearchButton}
										disabled={displayLoader || isSubmitting}
									/>

									<div className="search-form__last-search">
										<LastSearchContainer
											websiteType={WEBSITE_TUNNEL_TYPES.STOPOVER}
											updateFloatingPositionReference={
												updateFloatingPositionReference
											}
											onFocus={onFieldFocus}
										/>
									</div>
								</div>
								<div className="search-form__first-line-break" />
							</Form>
						</>
					);
				}}
			</Formik>
		</div>
	);
};

SmartDPSearchForm.propTypes = {
	onSubmit: PropTypes.func,
	onSuccess: PropTypes.func,
	departureCities: PropTypes.array,
	stopoverCities: PropTypes.array,
	stopoverDirections: PropTypes.array,
	destinations: PropTypes.array,
	cabins: PropTypes.array,
	initialValues: PropTypes.object,
	validateOnMount: PropTypes.bool,
	handleFormError: PropTypes.func,
	onFieldFocus: PropTypes.func,
	displayLoader: PropTypes.bool,
	brand: brandPropTypes,
	isAutoScrollActive: PropTypes.bool,
	errorScrollIntoView: PropTypes.bool,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default memo(SmartDPSearchForm);
